import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCards } from '../../pages/CardsApp';

const ActivateModal = ({ isOpen, onClose, card }) => {
  const { t } = useTranslation();
  const { activateCard } = useCards();
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (previousFocusRef.current && !isOpen) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    if (modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        setTimeout(() => {
          firstElement.focus();
        }, 100);

        const trapFocus = (e) => {
          if (e.key === 'Tab' && !e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
          if (e.key === 'Tab' && e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        };

        document.addEventListener('keydown', trapFocus);

        return () => {
          document.removeEventListener('keydown', handleKeyDown);
          document.removeEventListener('keydown', trapFocus);
        };
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    const success = await activateCard(card.id);
    if (success) {
      onClose();
    }
  };

  if (!isOpen || !card) return null;

  return (
    <div
      id="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
      style={{ display: 'block' }}
      aria-hidden="false"
    >
      <div id="modalBox" className="centerPopup" tabIndex="-1" ref={modalRef}>
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="modal-title">
              {t('Activer la carte')}
            </h3>
          </div>
          <div className="modal-body" id="activateModal">
            {card.is_physical ? (
              <p>
                {t('En demandant l\'activation de votre carte vous confirmez l\'avoir bien reçue et acceptez la pleine responsabilité en cas de perte ou vol ultérieur')}
              </p>
            ) : (
              <p>
                {t('En demandant l\'activation de votre carte vous confirmez l\'avoir bien reçue. Votre carte sera activée dans les 24 heures')}
              </p>
            )}
          </div>
          <div id="activateModalFooter" className="modal-footer">
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleConfirm}
            >
              OK
            </button>
            <button
              className="btn btn-default"
              type="button"
              onClick={handleClose}
            >
              {t('Annuler')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateModal;